import { useEffect, useState } from 'react'
import { fetchTodayPuzzle, fetchPuzzleByDate, type TodayPuzzleResponse, type PuzzleResponse } from './lib/api'
import { useGameState } from './hooks/useGameState'
import { getCurrentISTDate } from './lib/utils'
import DailyWord from './components/games/DailyWord'
import PicReveal from './components/games/PicReveal'
import RankRush from './components/games/RankRush'

function App() {
  const { state, updateState } = useGameState()
  const [todayPuzzle, setTodayPuzzle] = useState<TodayPuzzleResponse | null>(null)
  const [fullPuzzle, setFullPuzzle] = useState<PuzzleResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const today = getCurrentISTDate()

  useEffect(() => {
    async function loadPuzzle() {
      try {
        setLoading(true)
        const puzzle = await fetchTodayPuzzle()
        setTodayPuzzle(puzzle)
        
        // Fetch full puzzle data (with answers) for client-side validation
        // Note: For MVP, we fetch historical puzzle which includes answers
        // In production, you might want to obfuscate or validate server-side
        try {
          const full = await fetchPuzzleByDate(puzzle.date)
          setFullPuzzle(full)
        } catch (err) {
          // If historical fetch fails, we'll work with what we have
          console.warn('Could not fetch full puzzle data:', err)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load puzzle')
      } finally {
        setLoading(false)
      }
    }
    loadPuzzle()
  }, [])

  // Update last played date if different
  useEffect(() => {
    if (today !== state.last_played) {
      updateState({ last_played: today })
    }
  }, [today, state.last_played, updateState])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading today's puzzles...</p>
        </div>
      </div>
    )
  }

  if (error || !todayPuzzle) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center bg-white rounded-lg shadow-lg p-8 max-w-md">
          <p className="text-red-600 mb-4">⚠️ {error || 'Failed to load puzzle'}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  // If we don't have full puzzle data, show a message
  if (!fullPuzzle) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center bg-white rounded-lg shadow-lg p-8 max-w-md">
          <p className="text-gray-600 mb-4">Puzzle data is being prepared. Please check back soon!</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Refresh
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
      <div className="container mx-auto px-4 py-6 md:py-8">
        <header className="text-center mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            DailyGuess 🇮🇳
          </h1>
          <p className="text-gray-600 mb-2">
            Your daily puzzle ritual
          </p>
          <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
            <span>📅 {todayPuzzle.date}</span>
            {state.streak > 0 && (
              <span>🔥 Streak: {state.streak} days</span>
            )}
          </div>
        </header>

        <main className="max-w-6xl mx-auto space-y-6">
          {/* Daily Word */}
          {todayPuzzle.word && fullPuzzle.word && (
            <DailyWord
              puzzle={fullPuzzle.word}
              gameState={state.games.daily_word}
              onComplete={(result) => {
                updateState({
                  games: {
                    ...state.games,
                    daily_word: result
                  }
                })
              }}
            />
          )}

          {/* Pic Reveal */}
          {todayPuzzle.pic && fullPuzzle.pic && (
            <PicReveal
              imageUrl={fullPuzzle.pic.url}
              imageId={todayPuzzle.pic.id}
              answer={fullPuzzle.pic.canonical_answer}
              aliases={fullPuzzle.pic.aliases}
              gameState={state.games.pic_reveal}
              onComplete={(result) => {
                updateState({
                  games: {
                    ...state.games,
                    pic_reveal: result
                  }
                })
              }}
            />
          )}

          {/* Rank Rush */}
          {todayPuzzle.rank && fullPuzzle.rank && (
            <RankRush
              items={fullPuzzle.rank.items}
              solutionOrder={fullPuzzle.rank.solution_order}
              gameState={state.games.rank_rush}
              context="population"
              onComplete={(result) => {
                updateState({
                  games: {
                    ...state.games,
                    rank_rush: result
                  }
                })
              }}
            />
          )}
        </main>
      </div>
    </div>
  )
}

export default App

